import { isEmpty } from 'lodash';

import { getResponse } from '@satellite/services/serviceUtilities';
import { cacheableGet } from '@satellite/services/httpClient';

export default {
  /**
   * @template TResponse
   * Fetch spot grid data for a warehouse
   * @param {string} warehouseId - Warehouse ID
   * @param {Object} [options] - Request options
   * @param {import('@satellite/services/serviceUtilities').GetResponseConfig} [options.getResponseConfig] - Response handling options (includeMetaData)
   * @param {import('axios').AxiosRequestConfig} [options.requestConfig] - Axios request config (params, signal, headers, etc.)
   * @param {boolean} [options.enableCache] - Enable inflight request caching (default: true)
   * @returns {Promise<TResponse>}
   */
  async getSpotGrid(
    warehouseId,
    { getResponseConfig = {}, requestConfig = {}, enableCache = true } = {}
  ) {
    const axiosResponse = await cacheableGet('yard/view/spot-grid', {
      ...requestConfig,
      params: { ...requestConfig.params, warehouseId },
      enableCache
    });

    return getResponse(axiosResponse, getResponseConfig);
  },

  /**
   * @template TResponse
   * Fetch waiting assets for a warehouse
   * @param {string} warehouseId - Warehouse ID
   * @param {Object} [options] - Request options
   * @param {import('@satellite/services/serviceUtilities').GetResponseConfig} [options.getResponseConfig] - Response handling options (includeMetaData)
   * @param {import('axios').AxiosRequestConfig} [options.requestConfig] - Axios request config (params, signal, headers, etc.)
   * @param {boolean} [options.enableCache] - Enable inflight request caching (default: true)
   * @returns {Promise<TResponse>}
   */
  async getWaitingAssets(
    warehouseId,
    { getResponseConfig = {}, requestConfig = {}, enableCache = true } = {}
  ) {
    const axiosResponse = await cacheableGet('yard/view/waiting-assets', {
      ...requestConfig,
      params: { ...requestConfig.params, warehouseId },
      enableCache
    });

    return getResponse(axiosResponse, getResponseConfig);
  },

  /**
   * @template TResponse
   * Fetch the warehouse's selectable asset containers with their open move and Check Asset tasks.
   * @param {string} warehouseId - Warehouse ID
   * @param {Object} [options] - Request options
   * @param {import('@satellite/services/serviceUtilities').GetResponseConfig} [options.getResponseConfig] - Response handling options (includeMetaData)
   * @param {import('axios').AxiosRequestConfig} [options.requestConfig] - Axios request config (params, signal, headers, etc.)
   * @param {boolean} [options.enableCache] - Enable inflight request caching (default: true)
   * @returns {Promise<TResponse>}
   */
  async getAssets(
    warehouseId,
    { getResponseConfig = {}, requestConfig = {}, enableCache = true } = {}
  ) {
    const axiosResponse = await cacheableGet('yard/view/assets', {
      ...requestConfig,
      params: { ...requestConfig.params, warehouseId },
      enableCache
    });

    return getResponse(axiosResponse, getResponseConfig);
  },

  /**
   * @template TResponse
   * Fetch standalone asset containers whose latest status is Missing for a warehouse.
   * @param {string} warehouseId - Warehouse ID
   * @param {Object} [options] - Request options
   * @param {import('@satellite/services/serviceUtilities').GetResponseConfig} [options.getResponseConfig] - Response handling options (includeMetaData)
   * @param {import('axios').AxiosRequestConfig} [options.requestConfig] - Axios request config (params, signal, headers, etc.)
   * @param {boolean} [options.enableCache] - Enable inflight request caching (default: true)
   * @returns {Promise<TResponse>}
   */
  async getMissingAssets(
    warehouseId,
    { getResponseConfig = {}, requestConfig = {}, enableCache = true } = {}
  ) {
    const axiosResponse = await cacheableGet('yard/view/missing-assets', {
      ...requestConfig,
      params: { ...requestConfig.params, warehouseId },
      enableCache
    });

    return getResponse(axiosResponse, getResponseConfig);
  },

  /**
   * @template TResponse
   * Fetch asset containers whose latest status is In Transit for a warehouse.
   * @param {string} warehouseId - Warehouse ID
   * @param {Object} [options] - Request options
   * @param {import('@satellite/services/serviceUtilities').GetResponseConfig} [options.getResponseConfig] - Response handling options (includeMetaData)
   * @param {import('axios').AxiosRequestConfig} [options.requestConfig] - Axios request config (params, signal, headers, etc.)
   * @param {boolean} [options.enableCache] - Enable inflight request caching (default: true)
   * @returns {Promise<TResponse>}
   */
  async getInTransitAssets(
    warehouseId,
    { getResponseConfig = {}, requestConfig = {}, enableCache = true } = {}
  ) {
    const axiosResponse = await cacheableGet('yard/view/in-transit-assets', {
      ...requestConfig,
      params: { ...requestConfig.params, warehouseId },
      enableCache
    });

    return getResponse(axiosResponse, getResponseConfig);
  },

  /**
   * @template TResponse
   * Fetch list view rows for a warehouse
   * @param {string} warehouseId - Warehouse ID
   * @param {Object} [options] - Request options
   * @param {boolean} [options.includeWaiting] - Include waiting assets (default: true)
   * @param {boolean} [options.includeSpotAssignedAt] - Ask for `spotAssignedAt`; costs the BFF two extra queries over `spotevent` (default: false)
   * @param {boolean} [options.includeCheckInFormData] - Merge the asset visit's check-in answers into `customFormData`; costs the BFF one extra query (default: false)
   * @param {import('@satellite/services/serviceUtilities').GetResponseConfig} [options.getResponseConfig] - Response handling options (includeMetaData)
   * @param {import('axios').AxiosRequestConfig} [options.requestConfig] - Axios request config (params, signal, headers, etc.)
   * @param {boolean} [options.enableCache] - Enable inflight request caching (default: true)
   * @returns {Promise<{ rows: Array<Record<string, unknown> & { spotReserves: Array<{ id: string, isActive: boolean, entityName?: string|null, objectId?: string|null, validUntil?: string|null }> }> }>}
   * Each row includes `spotReserves` (active reserves, same shape as spot-grid) for rendering reserve refs and cache invalidation; "is reserved" is derived from `Spot.status` (RESERVED_MANUAL / RESERVED_AUTOMATED), not from this array length.
   */
  async getListViewRows(
    warehouseId,
    {
      includeWaiting = true,
      includeSpotAssignedAt = false,
      includeCheckInFormData = false,
      getResponseConfig = {},
      requestConfig = {},
      enableCache = true
    } = {}
  ) {
    const axiosResponse = await cacheableGet('yard/view/list-rows', {
      ...requestConfig,
      params: {
        ...requestConfig.params,
        warehouseId,
        includeWaiting,
        includeSpotAssignedAt,
        includeCheckInFormData
      },
      enableCache
    });

    return getResponse(axiosResponse, getResponseConfig);
  },

  /**
   * @template TResponse
   * Resolve a warehouse-scoped inventory check task's scope into grouped asset containers and
   * progress counts (the "open inventory" BFF — OD-3625). With only `taskId` the task's stored
   * inventory groups are resolved; `types` (`missing` / `unassigned`) and `spotAreaIds` override
   * that selection so the listing can show groups the task never stored.
   * @param {string} taskId - Yard task id
   * @param {Object} [options] - Request options
   * @param {Array<'missing'|'unassigned'>} [options.types] - Optional group-type filter
   * @param {string[]} [options.spotAreaIds] - Optional spot-area filter
   * @param {import('@satellite/services/serviceUtilities').GetResponseConfig} [options.getResponseConfig]
   * @param {import('axios').AxiosRequestConfig} [options.requestConfig]
   * @param {boolean} [options.enableCache]
   * @returns {Promise<TResponse>} `{ summary: { total, checked, missing, pending }, groups: [...] }`
   */
  async getInventoryCheck(
    taskId,
    { types, spotAreaIds, getResponseConfig = {}, requestConfig = {}, enableCache = true } = {}
  ) {
    const axiosResponse = await cacheableGet('yard/view/inventory-check', {
      ...requestConfig,
      params: {
        ...requestConfig.params,
        taskId,
        ...(isEmpty(types) ? {} : { types }),
        ...(isEmpty(spotAreaIds) ? {} : { spotAreaIds })
      },
      enableCache
    });

    return getResponse(axiosResponse, getResponseConfig);
  },

  /**
   * Fetch warehouse-scoped inventory container counts (per active spot area + missing + waiting)
   * for the inventory check-task picker and cards.
   * @param {string} warehouseId - Warehouse ID
   * @param {Object} [options] - Request options
   * @param {import('@satellite/services/serviceUtilities').GetResponseConfig} [options.getResponseConfig]
   * @param {import('axios').AxiosRequestConfig} [options.requestConfig]
   * @param {boolean} [options.enableCache]
   * @returns {Promise<{ missing: number, waiting: number, total: number, byArea: Array<{ spotAreaId: string, spotAreaName: string, count: number }> }>}
   */
  async getInventoryCounts(
    warehouseId,
    { getResponseConfig = {}, requestConfig = {}, enableCache = true } = {}
  ) {
    const axiosResponse = await cacheableGet('yard/view/inventory-counts', {
      ...requestConfig,
      params: { ...requestConfig.params, warehouseId },
      enableCache
    });

    return getResponse(axiosResponse, getResponseConfig);
  },

  /**
   * Fetch spot detail for the drawer: spot, task, assignment, and when applicable
   * arrival/same-appointment containers (one request).
   * @param {string} spotId - Spot ID
   * @param {string} warehouseId - Warehouse ID
   * @param {Object} [options] - Request options
   * @param {import('@satellite/services/serviceUtilities').GetResponseConfig} [options.getResponseConfig]
   * @param {import('axios').AxiosRequestConfig} [options.requestConfig]
   * @param {boolean} [options.enableCache]
   * @returns {Promise<{ spot: object, openMoveTasks: Array<{ id: string, refNum: string|null }>, openCheckTasks: Array<{ id: string, refNum: string|null }>, arrivalContainers?: array, containersWithSamePickupAppointment?: array, containersWithSameDropAppointment?: array }>}
   */
  async getSpotDetail(
    spotId,
    warehouseId,
    { getResponseConfig = {}, requestConfig = {}, enableCache = true } = {}
  ) {
    const axiosResponse = await cacheableGet('yard/view/spot-detail', {
      ...requestConfig,
      params: { ...requestConfig.params, spotId, warehouseId },
      enableCache
    });

    return getResponse(axiosResponse, getResponseConfig);
  }
};
